import {
  type ButtonHTMLAttributes,
  type Dispatch,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type PropsWithChildren,
  type ReactElement,
  type SetStateAction,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  close: () => void;
  triggerRef: MutableRefObject<HTMLElement | null>;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(
  null,
);

const useDropdownMenuContext = () => {
  const context = useContext(DropdownMenuContext);
  if (!context) {
    throw new Error(
      "DropdownMenu components must be used within <DropdownMenu>.",
    );
  }
  return context;
};

export const DropdownMenu = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const value = useMemo(
    () => ({ open, setOpen, close, triggerRef }),
    [open, close],
  );

  return (
    <DropdownMenuContext.Provider value={value}>
      <div ref={rootRef} className={clsx("relative inline-flex", className)}>
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
};

type TriggerChildProps = {
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLElement>) => void;
  disabled?: boolean;
  "aria-disabled"?: boolean;
};

type DropdownMenuTriggerProps = { children: ReactElement<TriggerChildProps> };

export const DropdownMenuTrigger = forwardRef<
  HTMLElement,
  DropdownMenuTriggerProps
>(({ children }, forwardedRef) => {
  const { open, setOpen, triggerRef } = useDropdownMenuContext();

  if (!isValidElement(children)) {
    throw new Error(
      "DropdownMenuTrigger requires a single React element child.",
    );
  }

  const child = children as ReactElement<TriggerChildProps>;
  const disabled = Boolean(
    child.props.disabled ?? child.props["aria-disabled"],
  );

  const setRefs = (node: HTMLElement | null) => {
    triggerRef.current = node;
    if (typeof forwardedRef === "function") {
      forwardedRef(node);
    } else if (forwardedRef) {
      forwardedRef.current = node;
    }
  };

  const handleClick = (event: ReactMouseEvent<HTMLElement>) => {
    child.props.onClick?.(event);
    if (event.defaultPrevented || disabled) {
      return;
    }
    setOpen((currentOpen) => !currentOpen);
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    child.props.onKeyDown?.(event);
    if (event.defaultPrevented || disabled) {
      return;
    }
    if (
      event.key === "ArrowDown" ||
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      setOpen(true);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const triggerProps = {
    ref: setRefs,
    "aria-haspopup": "menu",
    "aria-expanded": open,
    "data-state": open ? "open" : "closed",
    onClick: handleClick,
    onKeyDown: handleKeyDown,
  } as unknown as Partial<TriggerChildProps> & {
    ref?: (node: HTMLElement | null) => void;
  };

  return cloneElement(child, triggerProps);
});

DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

type DropdownMenuContentProps = PropsWithChildren<{
  className?: string;
  align?: "start" | "end";
}>;

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  DropdownMenuContentProps
>(({ className, align = "start", children }, forwardedRef) => {
  const { open, close, triggerRef } = useDropdownMenuContext();
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const firstItem = contentRef.current?.querySelector<HTMLButtonElement>(
      "[data-menu-item]:not(:disabled)",
    );
    firstItem?.focus();
  }, [open]);

  const setRefs = (node: HTMLDivElement | null) => {
    contentRef.current = node;
    if (typeof forwardedRef === "function") {
      forwardedRef(node);
    } else if (forwardedRef) {
      forwardedRef.current = node;
    }
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      triggerRef.current?.focus();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }
    event.preventDefault();
    const items = Array.from(
      contentRef.current?.querySelectorAll<HTMLButtonElement>(
        "[data-menu-item]:not(:disabled)",
      ) ?? [],
    );
    if (!items.length) {
      return;
    }
    const currentIndex = items.indexOf(
      document.activeElement as HTMLButtonElement,
    );
    const nextIndex =
      event.key === "ArrowDown"
        ? currentIndex === -1 || currentIndex === items.length - 1
          ? 0
          : currentIndex + 1
        : currentIndex <= 0
          ? items.length - 1
          : currentIndex - 1;
    items[nextIndex]?.focus();
  };

  if (!open) return null;

  return (
    <div
      ref={setRefs}
      role="menu"
      tabIndex={-1}
      className={clsx(
        "absolute z-20 mt-2 min-w-[12rem] rounded-md border border-border-subtle bg-surface-primary py-1 shadow-lg focus:outline-none",
        align === "end" ? "right-0" : "left-0",
        className,
      )}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
});

DropdownMenuContent.displayName = "DropdownMenuContent";

type DropdownMenuItemProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick" | "type"
> & {
  icon?: ReactElement;
  shortcut?: string;
  onSelect?: () => void | Promise<void>;
};

export const DropdownMenuItem = forwardRef<
  HTMLButtonElement,
  DropdownMenuItemProps
>(
  (
    { className, children, icon, shortcut, onSelect, disabled, ...props },
    ref,
  ) => {
    const { close, triggerRef } = useDropdownMenuContext();
    const consumedByMouseUpRef = useRef(false);
    const selectionInFlightRef = useRef(false);

    const runSelect = async () => {
      if (disabled || selectionInFlightRef.current) return;
      selectionInFlightRef.current = true;
      try {
        await onSelect?.();
      } finally {
        selectionInFlightRef.current = false;
        close();
        triggerRef.current?.focus();
      }
    };

    const handleMouseUp = (event: ReactMouseEvent<HTMLButtonElement>) => {
      // Mouseup preserves standard press-and-release menu semantics while still
      // covering browsers that occasionally drop the subsequent click event.
      if (event.button !== 0 || disabled) return;
      consumedByMouseUpRef.current = true;
      void runSelect();
    };

    const handleClick = async (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (consumedByMouseUpRef.current) {
        consumedByMouseUpRef.current = false;
        return;
      }
      await runSelect();
    };

    const iconElement = icon as
      | ReactElement<{ className?: string }>
      | undefined;

    return (
      <button
        ref={ref}
        role="menuitem"
        type="button"
        data-menu-item
        className={clsx(
          "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-content-secondary transition-colors hover:bg-surface-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
          className,
        )}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        disabled={disabled}
        {...props}
      >
        {iconElement
          ? cloneElement(iconElement, {
              className: clsx("h-4 w-4 shrink-0", iconElement.props.className),
            })
          : null}
        <span className="flex-1">{children}</span>
        {shortcut ? (
          <span className="text-xs uppercase tracking-wide text-content-muted">
            {shortcut}
          </span>
        ) : null}
      </button>
    );
  },
);

DropdownMenuItem.displayName = "DropdownMenuItem";
